import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AWS_S3_URL } from "../../utils/constants";
import { Card, CardBody, Input } from "reactstrap";
import {
  PiFileLight, PiFileAudioLight, PiFilePdfLight, PiFileVideoLight,
  PiFileDocLight, PiFileImageLight, PiFileArchiveLight, PiFileCodeLight,
  PiFileCsvLight, PiFilePptLight,
} from "react-icons/pi";
import { BsTrash3 } from "react-icons/bs";
import { IoCreateOutline, IoDownloadOutline } from "react-icons/io5";

const TYPE_ICON = {
  'application/pdf': { icon: PiFilePdfLight, color: '#dc3545' },
  'audio': { icon: PiFileAudioLight, color: '#6f42c1' },
  'video': { icon: PiFileVideoLight, color: '#fd7e14' },
  'image': { icon: PiFileImageLight, color: '#0d6efd' },
  'text/csv': { icon: PiFileCsvLight, color: '#198754' },
  'application/vnd.ms-powerpoint': { icon: PiFilePptLight, color: '#fd7e14' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { icon: PiFilePptLight, color: '#fd7e14' },
  'application/zip': { icon: PiFileArchiveLight, color: '#ffc107' },
  'application/x-rar-compressed': { icon: PiFileArchiveLight, color: '#ffc107' },
  'text': { icon: PiFileCodeLight, color: '#20c997' },
  'word': { icon: PiFileDocLight, color: '#0d6efd' },
};

function resolveIcon(type) {
  if (!type) return { icon: PiFileLight, color: '#6c757d' };
  if (TYPE_ICON[type]) return TYPE_ICON[type];
  for (const [key, val] of Object.entries(TYPE_ICON)) {
    if (type.startsWith(key) || type.includes(key)) return val;
  }
  return { icon: PiFileLight, color: '#6c757d' };
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

function Attachment({ attachment, onDelete, onRename }) {
  const { t } = useTranslation();
  const [renaming, setRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState(attachment.attachment_title || '');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isImage = attachment.attachment_type?.startsWith('image/');
  const { icon: FileIcon, color } = resolveIcon(attachment.attachment_type);
  const fileUrl = AWS_S3_URL + attachment.attachment_path;
  const ext = attachment.attachment_type?.split('/')[1]?.toUpperCase();

  const handleRenameSubmit = () => {
    const trimmed = newTitle.trim();
    if (trimmed && trimmed !== attachment.attachment_title) {
      onRename?.(attachment.id, trimmed);
    }
    setRenaming(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleRenameSubmit();
    if (e.key === 'Escape') {
      setNewTitle(attachment.attachment_title || '');
      setRenaming(false);
    }
  };

  return (
    <Card className="mb-2" style={{ border: '1px solid #e9ecef' }}>
      <CardBody className="p-2">
        <div className="d-flex align-items-center gap-2">

          <div className="flex-shrink-0">
            {isImage ? (
              <img
                src={fileUrl}
                width={38}
                height={38}
                style={{ objectFit: 'cover', borderRadius: 6, border: '1px solid #dee2e6' }}
                alt=""
              />
            ) : (
              <FileIcon size={34} color={color} />
            )}
          </div>

          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            {renaming ? (
              <Input
                bsSize="sm"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                onBlur={handleRenameSubmit}
                onKeyDown={handleKeyDown}
                autoFocus
                className="py-0"
                style={{ height: 26 }}
              />
            ) : (
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="d-block fw-semibold text-dark text-decoration-none text-truncate"
                title={attachment.attachment_title}
                style={{ fontSize: 13 }}
              >
                {attachment.attachment_title}
              </a>
            )}
            <div className="text-muted" style={{ fontSize: 11, marginTop: 1 }}>
              {formatSize(attachment.attachment_size)}
              {ext && <span className="ms-1">· {ext}</span>}
            </div>
          </div>

          <div className="flex-shrink-0 d-flex align-items-center gap-1">
            {confirmDelete ? (
              <>
                <button
                  className="btn btn-danger btn-sm py-0 px-2"
                  style={{ fontSize: 11 }}
                  onClick={() => { setConfirmDelete(false); onDelete?.(attachment.id); }}
                >
                  {t('common.delete')}
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm py-0 px-2"
                  style={{ fontSize: 11 }}
                  onClick={() => setConfirmDelete(false)}
                >
                  {t('common.cancel')}
                </button>
              </>
            ) : (
              <>
                <a
                  href={fileUrl}
                  download
                  className="btn btn-sm btn-light p-1 text-secondary"
                  title={t('common.download')}
                  style={{ lineHeight: 1 }}
                  target="_blank"
                >
                  <IoDownloadOutline size={15} />
                </a>
                <button
                  className="btn btn-sm btn-light p-1 text-secondary"
                  title={t('common.rename')}
                  onClick={() => setRenaming(true)}
                  style={{ lineHeight: 1 }}
                >
                  <IoCreateOutline size={15} />
                </button>
                <button
                  className="btn btn-sm btn-light p-1 text-danger"
                  title={t('common.delete')}
                  onClick={() => setConfirmDelete(true)}
                  style={{ lineHeight: 1 }}
                >
                  <BsTrash3 size={13} />
                </button>
              </>
            )}
          </div>

        </div>
      </CardBody>
    </Card>
  );
}

export default Attachment;
